// src/helpers/fieldsForActivar.js
import { RECORD_FIELDS } from "../models/recordFields";
import { USER_FIELDS } from "../models/userFields";

/**
 * Obtiene los campos a mostrar en el formulario de activación
 * según el tipo de objeto y la vista actual.
 * 
 * TIPOS SOPORTADOS:
 * - mascota: Tags para perros, gatos y otras mascotas
 * - llavero: Tags para objetos (llaves, mochilas, etc.)
 * 
 * VISTAS SOPORTADAS:
 * - activar: Formulario inicial de activación (nuevo usuario)
 * - ligandoSiguiente: Formulario para enlazar a cuenta existente
 * - adminCreacion: Panel admin para crear registros
 * - adminEdicion: Panel admin para editar registros
 * - consultar: Vista pública de consulta
 * - userEdicion: Panel usuario para editar sus registros
 * - consultaDelVeterinario: Vista especial para veterinarios
 * 
 * @param {string} tipo - Tipo de objeto: "mascota" o "llavero"
 * @param {string} currentView - Vista actual del formulario
 * @returns {Array} Array de campos filtrados y ordenados
 * 
 * @example
 * const fields = getFieldsForActivar('mascota', 'activar');
 * // Retorna: [{ fieldKey: 'cuentaExistente', label: '...', ... }, ...]
 */
export function getFieldsForActivar(tipo, currentView) {
  // ══════════════════════════════════════════════════════════
  // 1. FILTRAR CAMPOS DE OBJETO (RECORD_FIELDS)
  // ══════════════════════════════════════════════════════════
  const recordFieldsArr = Object.entries(RECORD_FIELDS)
    .filter(([fieldKey, def]) => {
      // Verificar si el campo es visible en la vista actual
      if (!def.visibleIn?.includes(currentView)) {
        return false;
      }

      // Verificar si el campo es aplicable al tipo de objeto
      // Si applicableTypes está definido y no incluye el tipo actual, excluir
      if (def.applicableTypes && def.applicableTypes.length > 0) {
        if (!def.applicableTypes.includes(tipo)) {
          return false;
        }
      }

      return true;
    })
    .map(([fieldKey, def]) => {
      // Aplicar labels y placeholders dinámicos si existen
      let fieldDef = { fieldKey, ...def };
      
      // Si el campo tiene función getDynamicLabel, aplicarla
      if (def.getDynamicLabel && typeof def.getDynamicLabel === 'function') {
        const dynamicLabel = def.getDynamicLabel(tipo);
        fieldDef = { ...fieldDef, ...dynamicLabel };
      }
      
      // Si el campo tiene función getDynamicPlaceholder, aplicarla
      if (def.getDynamicPlaceholder && typeof def.getDynamicPlaceholder === 'function') {
        const dynamicPlaceholder = def.getDynamicPlaceholder(tipo);
        fieldDef = { ...fieldDef, ...dynamicPlaceholder };
      }
      
      return fieldDef;
    });

  // ══════════════════════════════════════════════════════════
  // 2. FILTRAR CAMPOS DE USUARIO (USER_FIELDS)
  // ══════════════════════════════════════════════════════════
  const userFieldsArr = Object.entries(USER_FIELDS)
    .filter(([fieldKey, def]) => {
      // Verificar si el campo es visible en la vista actual
      if (!def.visibleIn?.includes(currentView)) {
        return false;
      }

      // Verificar si el campo es aplicable al tipo de objeto
      // Si applicableTypes está definido y no incluye el tipo actual, excluir
      if (def.applicableTypes && def.applicableTypes.length > 0) {
        if (!def.applicableTypes.includes(tipo)) {
          return false;
        }
      }

      return true;
    })
    .map(([fieldKey, def]) => ({ fieldKey, ...def }));

  // ══════════════════════════════════════════════════════════
  // 3. COMBINAR Y ORDENAR
  // ══════════════════════════════════════════════════════════
  const allFields = [...recordFieldsArr, ...userFieldsArr];

  // Ordenar por orderList (campos sin orderList van al final)
  allFields.sort((a, b) => (a.orderList || 999) - (b.orderList || 999));

  return allFields;
}

/**
 * Obtiene los campos requeridos para un tipo específico
 * 
 * @param {string} tipo - Tipo de objeto: "mascota" o "llavero"
 * @param {string} currentView - Vista actual
 * @returns {Array} Array de fieldKeys de campos requeridos
 * 
 * @example
 * const required = getRequiredFields('mascota', 'activar');
 * // Retorna: ['correoPropietario', 'nombrePropietario', 'telefono', 'nombre']
 */
export function getRequiredFields(tipo, currentView) {
  const fields = getFieldsForActivar(tipo, currentView);
  
  return fields
    .filter(field => {
      // Verificar si tiene validation array que incluya 'required'
      if (Array.isArray(field.validation)) {
        return field.validation.includes('required');
      }
      // Verificar si validation es string 'required'
      if (field.validation === 'required') {
        return true;
      }
      // Verificar propiedad required directa
      return field.required === true;
    })
    .map(field => field.fieldKey);
}

/**
 * Obtiene los campos opcionales para un tipo específico
 * 
 * @param {string} tipo - Tipo de objeto: "mascota" o "llavero"
 * @param {string} currentView - Vista actual
 * @returns {Array} Array de campos opcionales
 */
export function getOptionalFields(tipo, currentView) {
  const fields = getFieldsForActivar(tipo, currentView);
  const requiredKeys = getRequiredFields(tipo, currentView);
  
  return fields.filter(field => !requiredKeys.includes(field.fieldKey));
}